import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Leaf, Users, ShoppingBag, Link2, Check, ArrowRight, ArrowLeft,
  Copy, AlertCircle, Globe, Building2, Send, Sparkles, Zap, BadgeCheck,
  ChevronDown, Landmark, Banknote, Globe2, Rocket, ExternalLink,
  Star, TrendingUp, Shield, CircleDot, Award, HandCoins
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { CompanyLogo } from "@/components/CompanyLogo";
import { FundingSection } from "@/components/dashboard/FundingSection";
import { PriorityActions } from "@/components/dashboard/PriorityActions";
import { Footer } from "@/components/Footer";

const ACTION_ICONS = { employees: Users, customers: ShoppingBag, suppliers: Link2 };

const MATCH_CONFIG = {
  high: { label: "High match", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", dot: "bg-emerald-400" },
  good: { label: "Good fit", color: "bg-blue-500/20 text-blue-300 border-blue-500/30", dot: "bg-blue-400" },
  explore: { label: "Worth exploring", color: "bg-amber-500/20 text-amber-300 border-amber-500/30", dot: "bg-amber-400" },
  needs_improvement: { label: "Needs improvement", color: "bg-orange-500/20 text-orange-300 border-orange-500/30", dot: "bg-orange-400" },
};

const TAG_STYLES = {
  "No equity required": "bg-emerald-500/10 text-emerald-300/80 border-emerald-500/20",
  "Equity": "bg-violet-500/10 text-violet-300/80 border-violet-500/20",
  "Low risk": "bg-sky-500/10 text-sky-300/80 border-sky-500/20",
  "Fast process": "bg-amber-500/10 text-amber-300/80 border-amber-500/20",
  "Large ticket": "bg-rose-500/10 text-rose-300/80 border-rose-500/20",
  "EU-backed": "bg-blue-500/10 text-blue-300/80 border-blue-500/20",
  "Impact-focused": "bg-teal-500/10 text-teal-300/80 border-teal-500/20",
  "KPI-linked": "bg-cyan-500/10 text-cyan-300/80 border-cyan-500/20",
  "Competitive rates": "bg-lime-500/10 text-lime-300/80 border-lime-500/20",
  "Concessional terms": "bg-indigo-500/10 text-indigo-300/80 border-indigo-500/20",
  "Regional focus": "bg-orange-500/10 text-orange-300/80 border-orange-500/20",
  "Sector-specific": "bg-fuchsia-500/10 text-fuchsia-300/80 border-fuchsia-500/20",
};

const SUB_CATEGORY_SECTIONS = {
  international: {
    grant: { label: "Grants (non-dilutive)", icon: Shield, desc: "Keep full ownership" },
    vc_investor: { label: "Climate VCs & Investors", icon: TrendingUp, desc: "Growth capital" },
    blended: { label: "Blended Finance", icon: HandCoins, desc: "Grant + loan combos" },
  },
  regional: {
    green_loan: { label: "Green Loans", icon: Banknote, desc: "Preferential green rates" },
    sustainability_linked: { label: "Sustainability-Linked Loans", icon: TrendingUp, desc: "KPI-tied interest" },
    capex_financing: { label: "Capex Financing", icon: Building2, desc: "Equipment & infrastructure" },
  },
};

// Financing action cards data
const FINANCING_OPTIONS = [
  {
    id: "international",
    icon: Globe,
    title: "Access Global Climate Funds",
    description: "Connect with international climate funds, impact VCs, and ESG investors",
    microcopy: "Based on your sustainability data",
    cta: "Explore Funds",
    filterCategory: "climate_fund",
    recommended: false,
  },
  {
    id: "regional",
    icon: Building2,
    title: "Get Green Financing",
    description: "Find green loans from regional banks and local financial institutions",
    microcopy: "Banks in your country",
    cta: "Find Banks",
    filterCategory: "regional_bank",
    recommended: false,
  },
  {
    id: "send-report",
    icon: Send,
    title: "Share Report with Financiers",
    description: "Let investors review your profile and reach out directly",
    microcopy: "No extra paperwork needed",
    cta: "Send Report",
    filterCategory: null,
    recommended: true,
    badge: "Fastest",
  },
];

function FundingItem({ opp, delay = 0 }) {
  const match = MATCH_CONFIG[opp.match_level] || MATCH_CONFIG.good;
  const hasUrl = opp.url && opp.url.startsWith("http");

  const inner = (
    <>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <h5 className="text-sm font-semibold text-white leading-tight group-hover:text-primary transition-colors">{opp.title}</h5>
          {hasUrl && <ExternalLink className="w-3 h-3 text-white/20 group-hover:text-primary transition-colors flex-shrink-0" />}
        </div>
        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-medium flex-shrink-0 ${match.color}`}>
          <div className={`w-1.5 h-1.5 rounded-full ${match.dot}`} />
          {match.label}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-2">
        <span className="text-[11px] text-white/40 font-medium">{opp.funding_type}</span>
        {opp.ticket_size && (
          <>
            <span className="text-white/15">|</span>
            <span className="text-[11px] text-white/40 font-medium">{opp.ticket_size}</span>
          </>
        )}
      </div>

      {opp.one_liner && (
        <p className="text-xs text-white/50 mb-2 leading-relaxed">{opp.one_liner}</p>
      )}

      {opp.why_you_qualify && (
        <div className="flex items-start gap-1.5 mb-2.5">
          <Star className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-amber-300/70 leading-relaxed">{opp.why_you_qualify}</p>
        </div>
      )}

      {opp.tags && opp.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {opp.tags.map((tag, j) => (
            <span
              key={`tag-${tag}`}
              className={`text-[10px] px-2 py-0.5 rounded-full border ${TAG_STYLES[tag] || "bg-white/5 text-white/40 border-white/10"}`}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.2 }}
      data-testid={`funding-item-${opp.title?.replace(/\s+/g, '-').toLowerCase().slice(0, 30)}`}
    >
      {hasUrl ? (
        <a
          href={opp.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-primary/30 transition-all cursor-pointer group"
        >
          {inner}
        </a>
      ) : (
        <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-white/15 transition-all group">
          {inner}
        </div>
      )}
    </motion.div>
  );
}

function ClimateDropdown({ items }) {
  const bestMatches = items.filter(o => o.match_level === "high");
  const sections = SUB_CATEGORY_SECTIONS.international;

  const grouped = {};
  for (const key of Object.keys(sections)) {
    grouped[key] = items.filter(o => o.sub_category === key);
  }

  return (
    <div className="p-4 space-y-4 max-h-[520px] overflow-y-auto" data-testid="climate-dropdown">
      {/* Best Matches */}
      {bestMatches.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-3.5 h-3.5 text-amber-400" />
            <h5 className="text-xs font-bold text-amber-300 uppercase tracking-wider">Best Matches for You</h5>
          </div>
          <div className="space-y-2.5">
            {bestMatches.slice(0, 3).map((opp, i) => (
              <FundingItem key={`best-${opp.title || i}`} opp={opp} delay={i * 0.04} />
            ))}
          </div>
        </div>
      )}

      {bestMatches.length > 0 && <Separator className="bg-white/[0.06]" />}

      {/* Categorized Sections */}
      {Object.entries(sections).map(([key, section]) => {
        const sectionItems = grouped[key] || [];
        if (sectionItems.length === 0) return null;
        const SectionIcon = section.icon;
        return (
          <div key={key}>
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <SectionIcon className="w-3.5 h-3.5 text-white/40" />
                <h5 className="text-xs font-bold text-white/60 uppercase tracking-wider">{section.label}</h5>
              </div>
              <span className="text-[10px] text-white/30">{section.desc}</span>
            </div>
            <div className="space-y-2.5">
              {sectionItems.map((opp, i) => (
                <FundingItem key={`${key}-${opp.title || i}`} opp={opp} delay={i * 0.04} />
              ))}
            </div>
          </div>
        );
      })}

      {items.length === 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-white/40">Analyzing funding matches...</p>
        </div>
      )}
    </div>
  );
}

function BankingDropdown({ items, country }) {
  const sections = SUB_CATEGORY_SECTIONS.regional;

  const grouped = {};
  for (const key of Object.keys(sections)) {
    grouped[key] = items.filter(o => o.sub_category === key);
  }

  return (
    <div className="p-4 space-y-4 max-h-[520px] overflow-y-auto" data-testid="banking-dropdown">
      {/* Country Header */}
      <div className="flex items-center gap-2">
        <Globe className="w-3.5 h-3.5 text-blue-400" />
        <h5 className="text-xs font-bold text-blue-300 uppercase tracking-wider">
          Banks available in {country || "your region"}
        </h5>
      </div>

      {/* Grouped by financing type */}
      {Object.entries(sections).map(([key, section]) => {
        const sectionItems = grouped[key] || [];
        if (sectionItems.length === 0) return null;
        const SectionIcon = section.icon;
        return (
          <div key={key}>
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <SectionIcon className="w-3.5 h-3.5 text-white/40" />
                <h5 className="text-xs font-bold text-white/60 uppercase tracking-wider">{section.label}</h5>
              </div>
              <span className="text-[10px] text-white/30">{section.desc}</span>
            </div>
            <div className="space-y-2.5">
              {sectionItems.map((opp, i) => (
                <FundingItem key={`${key}-${opp.title || i}`} opp={opp} delay={i * 0.04} />
              ))}
            </div>
          </div>
        );
      })}

      {items.length === 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-white/40">Finding banks in your region...</p>
        </div>
      )}
    </div>
  );
}

function FinancingCard({ option, fundingData, companyCountry, onSendReport }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = option.icon;
  const isRecommended = option.recommended;
  const isSendReport = option.id === "send-report";
  
  const relevantFunding = fundingData?.opportunities?.filter(opp => 
    opp.category === option.filterCategory
  ) || [];

  const highMatches = relevantFunding.filter(o => o.match_level === "high").length;

  const handleClick = () => {
    if (isSendReport) {
      onSendReport?.();
    } else {
      setExpanded(!expanded);
    }
  };
  
  return (
    <motion.div
      className="relative rounded-2xl border transition-all glass-card border-white/10 hover:border-white/20"
      data-testid={`financing-card-${option.id}`}
    >
      {/* Recommended Badge */}
      {option.badge && (
        <div className="absolute top-3 right-4 z-10">
          <Badge className="bg-primary text-primary-foreground border-0 px-3 py-1 text-xs font-semibold flex items-center gap-1.5">
            <Zap className="w-3 h-3" />
            {option.badge}
          </Badge>
        </div>
      )}

      <div className="p-6">
        <div className="flex flex-col h-full">
          {/* Icon */}
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-white/5">
            <Icon className="w-6 h-6 text-white/70" />
          </div>

          {/* Content */}
          <h4 className="text-lg font-bold mb-2 text-white/90" style={{ fontFamily: "Manrope, sans-serif" }}>
            {option.title}
          </h4>
          <p className="text-sm text-white/50 mb-3 leading-relaxed">
            {option.description}
          </p>

          {/* Microcopy + match count */}
          <div className="flex items-center gap-1.5 mb-4">
            <BadgeCheck className="w-3.5 h-3.5 text-white/40" />
            <span className="text-xs text-white/40">
              {option.microcopy}
            </span>
            {!isSendReport && highMatches > 0 && (
              <Badge className="bg-emerald-500/15 text-emerald-300 border-0 text-[10px] ml-1">
                {highMatches} strong match{highMatches > 1 ? "es" : ""}
              </Badge>
            )}
          </div>

          {/* CTA Button */}
          <div className="mt-auto">
            <Button
              onClick={handleClick}
              className="w-full rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10"
              data-testid={`financing-cta-${option.id}`}
            >
              {option.cta}
              {isSendReport ? (
                <ArrowRight className="w-4 h-4 ml-2" />
              ) : (
                <motion.div
                  animate={{ rotate: expanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="ml-2"
                >
                  <ChevronDown className="w-4 h-4" />
                </motion.div>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Dropdown Content */}
      <AnimatePresence>
        {expanded && !isSendReport && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-white/10 overflow-hidden"
          >
            {option.id === "international" ? (
              <ClimateDropdown items={relevantFunding} />
            ) : (
              <BankingDropdown items={relevantFunding} country={companyCountry} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function ActionCard({ action, companyId, onSurveyCreated }) {
  const [creating, setCreating] = useState(false);
  const Icon = ACTION_ICONS[action.id] || Users;
  const statusColors = {
    not_started: "bg-muted text-muted-foreground",
    in_progress: "bg-primary/10 text-primary",
    completed: "bg-primary/20 text-primary",
  };

  const handleCreateSurvey = async () => {
    setCreating(true);
    try {
      const data = await apiPost("/api/surveys", {
        company_id: companyId,
        survey_type: action.id,
      });
      const surveyUrl = `${window.location.origin}/survey/${data.id}`;
      await navigator.clipboard.writeText(surveyUrl);
      toast.success("Survey link copied to clipboard!");
      onSurveyCreated();
    } catch {
      toast.error("Could not create survey.");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async () => {
    const surveyUrl = `${window.location.origin}/survey/${action.survey_id}`;
    await navigator.clipboard.writeText(surveyUrl);
    toast.success("Link copied!");
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="glass-card p-6 flex flex-col h-full"
      data-testid={`action-card-${action.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        <Badge className={`${statusColors[action.status]} border-0 text-xs`} data-testid={`action-status-${action.id}`}>
          {action.status === "not_started" ? "Not started" : action.status === "in_progress" ? "In progress" : "Completed"}
        </Badge>
      </div>
      <h3 className="text-lg font-semibold text-white mb-1">{action.title}</h3>
      <p className="text-sm text-white/40 mb-2 leading-relaxed">{action.description}</p>
      <p className="text-xs text-primary/70 mb-4">{action.why_it_matters}</p>
      <div className="mt-auto">
        {action.status === "not_started" && (
          <Button
            data-testid={`create-survey-${action.id}`}
            onClick={handleCreateSurvey}
            disabled={creating}
            className="w-full rounded-full bg-primary text-primary-foreground btn-glow"
          >
            {creating ? "Creating..." : "Send survey"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
        {action.status === "in_progress" && (
          <Button
            data-testid={`copy-link-${action.id}`}
            onClick={handleCopyLink}
            variant="outline"
            className="w-full rounded-full border-primary/20 text-primary hover:bg-primary/10"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy survey link
          </Button>
        )}
        {action.status === "completed" && (
          <div className="flex items-center justify-center gap-2 text-primary">
            <Check className="w-5 h-5" />
            <span className="text-sm font-medium">Responses received</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function ActionHubPage() {
  const { companyId } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [fundingData, setFundingData] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      setError(false);
      setLoading(true);
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const data = await apiGet(`/api/dashboard/${companyId}`);
          if (!mountedRef.current) return;
          setDashboard(data);
          setLoading(false);
          // Load funding data in background
          loadFunding();
          return;
        } catch (err) {
          if (!mountedRef.current) return;
          if (attempt < 2) await new Promise(r => setTimeout(r, 1200 * (attempt + 1)));
        }
      }
      if (mountedRef.current) { setError(true); setLoading(false); }
    };
    
    const loadFunding = async () => {
      try {
        const data = await apiPost("/api/funding/generate", { company_id: companyId });
        if (mountedRef.current) setFundingData(data);
      } catch (err) {
        // Non-critical: funding loads in background, dashboard still usable
      }
    };
    
    load();
    return () => { mountedRef.current = false; };
  }, [companyId]);

  const fetchDashboard = async () => {
    try {
      const data = await apiGet(`/api/dashboard/${companyId}`);
      if (mountedRef.current) setDashboard(data);
    } catch (err) {
      // Refresh failure is non-critical
    }
  };

  const handleSendReport = () => {
    toast.success("Report sharing feature coming soon!", {
      description: "Financiers will be able to review your sustainability profile."
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="action-hub-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white font-medium mb-1">Preparing your Action Hub</p>
          <p className="text-sm text-white/40">Loading stakeholders, funding & priorities...</p>
        </div>
      </div>
    );
  }

  if (!dashboard && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            {error ? "Connection issue" : "Unavailable"}
          </h2>
          <p className="text-muted-foreground mb-4">
            {error ? "We couldn't reach the server. Please try again." : "No data found for this company."}
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => window.location.reload()} className="rounded-full bg-primary text-primary-foreground" data-testid="retry-btn">
              Try again
            </Button>
            <Link to={`/dashboard/${companyId}`}>
              <Button variant="outline" className="rounded-full border-primary/20 text-primary hover:bg-primary/10">
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { company, actions } = dashboard;

  return (
    <div className="flex flex-col min-h-screen aurora-glow" data-testid="action-hub-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => {
                localStorage.removeItem('olana_company_id');
                localStorage.removeItem('olana_user_id');
                localStorage.removeItem('olana_email');
                window.location.href = '/';
              }}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
              data-testid="logo-home-link"
            >
              <Leaf className="w-5 h-5 text-primary" />
              <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Olana</span>
            </button>
            <Separator orientation="vertical" className="h-5 bg-border/50 mx-2" />
            <CompanyLogo name={company?.name} logoUrl={company?.logo_url} />
            <span className="text-white font-semibold text-sm" data-testid="header-company-name">{company?.name}</span>
            {company?.country && (
              <span className="text-white/40 text-xs">&middot; {company.country}</span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8 md:py-12 space-y-8 w-full">
        {/* Back + Title */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Link
            to={`/dashboard/${companyId}`}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white transition-colors mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            Action Hub
          </h1>
          <p className="text-base text-white/40 mt-2">Engage stakeholders, explore funding, and act on priorities.</p>
        </motion.div>

        {/* Stakeholder Engagement */}
        <div>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-1 h-8 rounded-full bg-primary" />
            <div>
              <h3 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
                Stakeholder Engagement
              </h3>
              <p className="text-sm text-white/40">Send professional surveys to gather data that improves your sustainability profile accuracy.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {(actions || []).map((action) => (
              <ActionCard key={action.id} action={action} companyId={companyId} onSurveyCreated={() => fetchDashboard()} />
            ))}
          </div>
        </div>

        {/* Unlock Financing & Support */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-1 h-8 rounded-full bg-amber-400" />
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Unlock Financing & Support
                </h3>
                <Sparkles className="w-4 h-4 text-amber-400" />
              </div>
              <p className="text-sm text-white/40">Turn your sustainability progress into funding opportunities.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-start">
            {FINANCING_OPTIONS.map((option) => (
              <FinancingCard 
                key={option.id} 
                option={option} 
                fundingData={fundingData}
                companyCountry={company?.country}
                onSendReport={handleSendReport}
              />
            ))}
          </div>
        </motion.div>

        {/* Support Available — Subsidies */}
        <FundingSection companyId={companyId} />

        {/* Priority Actions — original rich component */}
        <PriorityActions companyId={companyId} />
      </div>

      {/* Footer - pinned at bottom */}
      <div className="shrink-0 mt-auto">
        <Footer />
      </div>
    </div>
  );
}
